/**
 * Utility functions for accordion auto-scroll functionality
 */

/**
 * Checks if an element extends below the browser viewport fold line
 * @param element - The DOM element to check
 * @returns true if the element extends below the viewport
 */
export function isElementBelowFold(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  // Check if the bottom of the element is below the viewport
  return rect.bottom > viewportHeight;
}

/**
 * Gets the absolute bottom position of an element relative to the document
 * @param element - The DOM element
 * @returns The bottom position in pixels from the top of the document
 */
export function getElementBottomPosition(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  return rect.bottom + scrollTop;
}

/**
 * Smoothly scrolls the window to show the bottom of an element
 * @param element - The DOM element to scroll to
 * @param offset - Additional pixels to scroll beyond the element bottom (default: 20)
 */
export function scrollToElement(element: HTMLElement, offset: number = 20): void {
  const bottomPosition = getElementBottomPosition(element);
  const targetScrollPosition = bottomPosition - window.innerHeight + offset;

  // Only scroll if we need to scroll down
  if (targetScrollPosition > window.pageYOffset) {
    window.scrollTo({
      top: targetScrollPosition,
      behavior: 'smooth',
    });
  }
}

/**
 * Waits for a specific CSS class to appear on an element, indicating animation completion
 * @param element - The DOM element to observe
 * @param className - The CSS class to wait for
 * @param timeout - Maximum time to wait in milliseconds (default: 1000)
 * @returns Promise that resolves when the class appears or timeout is reached
 */
export function waitForAnimationComplete(
  element: HTMLElement,
  className: string,
  timeout: number = 1000,
): Promise<void> {
  return new Promise((resolve) => {
    // Check if class already exists
    if (element.classList.contains(className)) {
      // Even if class exists, wait a bit for any ongoing animations
      setTimeout(resolve, 100);
      return;
    }

    const state = {
      timeoutId: undefined as NodeJS.Timeout | undefined,
      observer: undefined as MutationObserver | undefined,
    };

    const cleanup = () => {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      if (state.observer) {
        state.observer.disconnect();
      }
    };

    state.timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, timeout);

    state.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains(className)) {
            // Wait a bit more after class appears to ensure animation is truly complete
            setTimeout(() => {
              cleanup();
              resolve();
            }, 100);
          }
        }
      });
    });

    state.observer.observe(element, {
      attributes: true,
      attributeFilter: ['class'],
    });
  });
}

/**
 * Finds the accordion content element within an accordion tab
 * @param accordionTab - The accordion tab element
 * @returns The content element or null if not found
 */
export function findAccordionContent(accordionTab: HTMLElement): HTMLElement | null {
  return accordionTab.querySelector('.p-toggleable-content') as HTMLElement;
}

/**
 * Finds the active accordion tab element
 * @param accordionContainer - The accordion container element
 * @returns The active tab element or null if not found
 */
export function findActiveAccordionTab(accordionContainer: HTMLElement): HTMLElement | null {
  return accordionContainer.querySelector('.p-accordion-tab-active') as HTMLElement;
}
